import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, CalendarClock, ChevronLeft, ChevronRight, Heart, ImagePlus, Link2, Loader2, LogOut, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, photoSrc } from "@/lib/api";
import { CATEGORIES, CATEGORY_LABELS, TYPE_LABELS, TIME_SLOT_LABELS } from "@/data/content";

const ASPECTS = ["aspect-[2/3]", "aspect-[3/4]", "aspect-[4/5]", "aspect-[1/1]", "aspect-[16/10]", "aspect-[3/2]"];
const STATUS_LABELS = { neu: "Neu", bestaetigt: "Bestätigt", abgelehnt: "Abgelehnt", archiviert: "Archiviert" };
const STATUS_COLORS = { neu: "text-gold border-gold/40", bestaetigt: "text-emerald-400 border-emerald-400/40", abgelehnt: "text-terracotta border-terracotta/40", archiviert: "text-zinc-500 border-white/15" };

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/auth/login", { email, password });
      localStorage.setItem("sm_token", res.data.token);
      onLogin(res.data.user);
      toast.success(`Willkommen zurück, ${res.data.user.name}.`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Anmeldung fehlgeschlagen"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-6">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={submit}
        className="w-full max-w-md border border-white/10 bg-ink2 p-10"
        data-testid="admin-login-form"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">Leno Fotografie — Backoffice</p>
        <h1 className="mt-3 font-serif text-3xl text-white">Anmelden</h1>
        <div className="mt-8 space-y-6">
          <div>
            <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">E-Mail</label>
            <input data-testid="admin-pin-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@leno-fotografie.de" className="input-line" />
          </div>
          <div>
            <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Passwort</label>
            <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="input-line" />
          </div>
        </div>
        {error && (
          <p className="mt-4 border border-terracotta/40 bg-terracotta/10 px-4 py-3 text-xs text-terracotta" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-submit-button"
          type="submit"
          disabled={loading}
          className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-colors hover:bg-white disabled:opacity-50"
        >
          {loading && <Loader2 size={14} className="animate-spin" />}
          Einloggen
        </button>
        <Link to="/" data-testid="admin-back-home-link" className="mt-6 flex items-center justify-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 hover:text-gold">
          <ArrowLeft size={12} /> Zurück zur Website
        </Link>
      </motion.form>
    </div>
  );
}

function PhotoManager({ photos, reload }) {
  const fileRef = useRef(null);
  const [mode, setMode] = useState("file");
  const [form, setForm] = useState({ title: "", category: "hochzeit", subtitle: "", year: "", aspect: "aspect-[4/5]", url: "" });
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "file") {
        const file = fileRef.current?.files?.[0];
        if (!file) {
          toast.error("Bitte eine Bilddatei auswählen.");
          setBusy(false);
          return;
        }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", form.title);
        fd.append("category", form.category);
        fd.append("subtitle", form.subtitle);
        fd.append("year", form.year);
        fd.append("aspect", form.aspect);
        await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post("/photos/url", {
          url: form.url,
          title: form.title,
          category: form.category,
          subtitle: form.subtitle,
          year: form.year,
          aspect: form.aspect,
        });
      }
      toast.success("Foto hinzugefügt — sofort in der Galerie sichtbar.");
      setForm({ title: "", category: "hochzeit", subtitle: "", year: "", aspect: "aspect-[4/5]", url: "" });
      if (fileRef.current) fileRef.current.value = "";
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/photos/${id}`);
      toast.success("Foto entfernt.");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= photos.length) return;
    const arr = [...photos];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try {
      await api.post("/photos/reorder", { ids: arr.map((p) => p.id) });
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <form onSubmit={upload} className="border border-white/10 bg-ink p-6" data-testid="admin-upload-form">
        <div className="mb-6 flex gap-3">
          <button type="button" data-testid="admin-mode-file" onClick={() => setMode("file")} className={`flex items-center gap-2 rounded-full px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${mode === "file" ? "bg-gold text-ink" : "border border-white/15 text-zinc-400"}`}>
            <Upload size={12} /> Datei-Upload
          </button>
          <button type="button" data-testid="admin-mode-url" onClick={() => setMode("url")} className={`flex items-center gap-2 rounded-full px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${mode === "url" ? "bg-gold text-ink" : "border border-white/15 text-zinc-400"}`}>
            <Link2 size={12} /> Per URL
          </button>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {mode === "file" ? (
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Bilddatei (jpg, png, webp · max. 15 MB)</label>
              <input ref={fileRef} data-testid="admin-file-input" type="file" accept="image/*" className="input-line file:mr-4 file:rounded-full file:border-0 file:bg-gold file:px-4 file:py-1.5 file:font-mono file:text-[10px] file:uppercase file:text-ink" />
            </div>
          ) : (
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Bild-URL</label>
              <input data-testid="admin-url-input" required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" className="input-line" />
            </div>
          )}
          <div>
            <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Titel *</label>
            <input data-testid="admin-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titel der Arbeit" className="input-line" />
          </div>
          <div>
            <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Kategorie</label>
            <select data-testid="admin-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-line cursor-pointer">
              {CATEGORIES.filter((c) => c.key !== "alle").map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Format</label>
            <select data-testid="admin-aspect-select" value={form.aspect} onChange={(e) => setForm({ ...form, aspect: e.target.value })} className="input-line cursor-pointer">
              {ASPECTS.map((a) => (
                <option key={a} value={a}>{a.replace("aspect-[", "").replace("]", "")}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Untertitel</label>
            <input data-testid="admin-subtitle-input" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="z. B. Namen, Ort" className="input-line" />
          </div>
          <div>
            <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Jahr</label>
            <input data-testid="admin-year-input" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="2026" className="input-line" />
          </div>
        </div>
        <button
          data-testid="admin-add-photo-button"
          type="submit"
          disabled={busy}
          className="mt-7 flex items-center gap-2 rounded-full bg-gold px-8 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-ink transition-colors hover:bg-white disabled:opacity-50"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
          Foto veröffentlichen
        </button>
      </form>

      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {photos.map((p, idx) => (
          <div key={p.id} className="group relative border border-white/10 bg-ink" data-testid={`admin-photo-${p.id}`}>
            <img src={photoSrc(p)} alt={p.title} className="aspect-square w-full object-cover" loading="lazy" />
            <div className="p-3">
              <p className="truncate font-serif text-sm text-white">{p.title}</p>
              <p className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500">
                {CATEGORY_LABELS[p.category]} · {p.year}
              </p>
            </div>
            <div className="flex items-center justify-between border-t border-white/10 px-2 py-1.5">
              <button
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                data-testid={`photo-move-left-${p.id}`}
                aria-label="Nach vorne"
                className="flex h-7 w-7 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-gold hover:text-ink disabled:opacity-25"
              >
                <ChevronLeft size={13} />
              </button>
              <span className="font-mono text-[8px] uppercase tracking-widest text-zinc-600">Pos. {idx + 1}</span>
              <button
                onClick={() => move(idx, 1)}
                disabled={idx === photos.length - 1}
                data-testid={`photo-move-right-${p.id}`}
                aria-label="Nach hinten"
                className="flex h-7 w-7 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-gold hover:text-ink disabled:opacity-25"
              >
                <ChevronRight size={13} />
              </button>
            </div>
            <button
              data-testid="admin-delete-photo-button"
              onClick={() => remove(p.id)}
              className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-ink/80 text-terracotta opacity-0 transition-opacity hover:bg-terracotta hover:text-white group-hover:opacity-100"
              aria-label="Foto löschen"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const fmtCreated = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

function ReplyBox({ endpoint, label }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(endpoint, { message: text.trim() });
      toast.success(`Nachricht an ${label} per E-Mail verschickt.`);
      setText("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-4 border-t border-white/10 pt-4">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder={`Deine Antwort an ${label} — wird per E-Mail verschickt`}
        className="input-line resize-none"
        data-testid="reply-textarea"
      />
      <button
        onClick={send}
        disabled={sending || !text.trim()}
        className="mt-3 rounded-full bg-gold px-6 py-2.5 font-mono text-[9px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-white disabled:opacity-50"
        data-testid="reply-send-button"
      >
        {sending ? "Senden …" : "Per E-Mail senden"}
      </button>
    </div>
  );
}

function PromoControl() {
  const [promo, setPromo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/promo").then((r) => setPromo(r.data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const toggle = async () => {
    setBusy(true);
    try {
      const res = await api.patch("/promo", { active: !promo.active });
      setPromo(res.data);
      toast.success(res.data.active ? "Gratis-Aktion ist jetzt sichtbar." : "Gratis-Aktion gestoppt — Banner verschwindet von der Website.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!promo) return null;
  return (
    <div className="mb-8 flex flex-col gap-4 border border-gold/30 bg-gold/5 p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="promo-control">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Gratis-Aktion — die ersten 3 Shootings kostenlos</p>
        <p className="mt-2 text-sm text-zinc-300" data-testid="promo-control-status">
          {promo.active
            ? promo.remaining > 0
              ? `Aktiv — noch ${promo.remaining} von ${promo.total} Plätzen frei (${promo.claimed} vergeben)`
              : `Aktiv, aber alle ${promo.total} Plätze sind bereits vergeben`
            : "Gestoppt — das Banner ist auf der Website nicht sichtbar"}
        </p>
      </div>
      <button
        onClick={toggle}
        disabled={busy}
        data-testid="promo-toggle-button"
        className={`shrink-0 rounded-full px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors disabled:opacity-50 ${
          promo.active ? "border border-terracotta/50 text-terracotta hover:bg-terracotta hover:text-white" : "bg-gold text-ink hover:bg-white"
        }`}
      >
        {promo.active ? "Aktion stoppen" : "Aktion starten"}
      </button>
    </div>
  );
}

function BookingsList({ bookings, reload }) {
  const [replyTo, setReplyTo] = useState(null);
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const archivePast = async () => {
    try {
      const res = await api.post("/bookings/archive-past");
      toast.success(`${res.data.archived} vergangene Termine ins Archiv verschoben.`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const visibleBookings = showArchived ? bookings : bookings.filter((b) => b.status !== "archiviert");
  const archivedCount = bookings.length - visibleBookings.length;

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      toast.success("Status aktualisiert — der Kunde bekommt eine E-Mail.");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveNote = async (id) => {
    setNoteSaving(true);
    try {
      await api.patch(`/bookings/${id}`, { note: noteText });
      toast.success("Notiz gespeichert — nur für dich sichtbar.");
      setNoteFor(null);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setNoteSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Termin wirklich löschen? Das kann nicht rückgängig gemacht werden.")) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Termin gelöscht.");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500" data-testid="bookings-count">
          {visibleBookings.length} sichtbar{archivedCount > 0 ? ` · ${archivedCount} archiviert` : ""}
        </p>
        <div className="flex gap-2">
          {archivedCount > 0 && (
            <button
              onClick={() => setShowArchived(!showArchived)}
              data-testid="toggle-archive-button"
              className="rounded-full border border-white/15 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:border-gold hover:text-gold"
            >
              {showArchived ? "Archiv verbergen" : "Archiv anzeigen"}
            </button>
          )}
          <button
            onClick={archivePast}
            data-testid="archive-past-button"
            className="rounded-full border border-white/15 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:border-gold hover:text-gold"
          >
            Vergangene aufräumen
          </button>
        </div>
      </div>
      {visibleBookings.length === 0 && (
        <p className="border border-white/10 bg-ink p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-600" data-testid="bookings-empty">
          Keine Terminanfragen
        </p>
      )}
      {visibleBookings.map((b) => (
        <div key={b.id} className="border border-white/10 bg-ink p-6" data-testid={`booking-row-${b.id}`}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-serif text-xl text-white">{b.name}</p>
                <span className={`rounded-full border px-3 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] ${STATUS_COLORS[b.status]}`}>{STATUS_LABELS[b.status]}</span>
                {b.promo && (
                  <span className="rounded-full bg-gold px-3 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-ink" data-testid={`booking-promo-${b.id}`}>
                    Gratis
                  </span>
                )}
              </div>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {b.date}
                {b.time_slot ? ` · ${TIME_SLOT_LABELS[b.time_slot] || b.time_slot}` : ""} · {TYPE_LABELS[b.shoot_type] || b.shoot_type} · {b.email}
              </p>
              {b.created_at && (
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600">Eingegangen am {fmtCreated(b.created_at)} Uhr</p>
              )}
              {b.message && <p className="mt-3 max-w-xl text-sm text-zinc-400">{b.message}</p>}
              {b.note && (
                <p className="mt-2 max-w-xl border-l-2 border-gold/50 pl-3 font-mono text-[11px] leading-relaxed text-gold/80" data-testid={`booking-note-${b.id}`}>
                  Notiz: {b.note}
                </p>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <button data-testid="booking-confirm-button" onClick={() => setStatus(b.id, "bestaetigt")} className="rounded-full border border-emerald-400/40 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400 transition-colors hover:bg-emerald-400 hover:text-ink">
                Bestätigen
              </button>
              <button data-testid="booking-decline-button" onClick={() => setStatus(b.id, "abgelehnt")} className="rounded-full border border-terracotta/40 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-terracotta transition-colors hover:bg-terracotta hover:text-white">
                Ablehnen
              </button>
              <button
                data-testid={`booking-reply-button-${b.id}`}
                onClick={() => setReplyTo(replyTo === b.id ? null : b.id)}
                className="rounded-full border border-white/20 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:border-gold hover:text-gold"
              >
                Antworten
              </button>
              <button
                data-testid={`booking-note-button-${b.id}`}
                onClick={() => {
                  setNoteFor(noteFor === b.id ? null : b.id);
                  setNoteText(b.note || "");
                }}
                className="rounded-full border border-white/20 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:border-gold hover:text-gold"
              >
                Notiz
              </button>
              <button
                data-testid={`booking-delete-button-${b.id}`}
                onClick={() => remove(b.id)}
                className="flex items-center gap-1 rounded-full border border-white/15 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 transition-colors hover:border-terracotta hover:text-terracotta"
              >
                <Trash2 size={11} /> Löschen
              </button>
            </div>
          </div>
          {replyTo === b.id && <ReplyBox endpoint={`/bookings/${b.id}/message`} label={b.name} />}
          {noteFor === b.id && (
            <div className="mt-4 border-t border-white/10 pt-4" data-testid={`booking-note-editor-${b.id}`}>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={2}
                placeholder="Private Notiz, z. B. Location, Adresse des Kunden, besondere Wünsche — sieht nur du"
                className="input-line resize-none"
                data-testid="note-textarea"
              />
              <button
                onClick={() => saveNote(b.id)}
                disabled={noteSaving}
                className="mt-3 rounded-full bg-gold px-6 py-2.5 font-mono text-[9px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-white disabled:opacity-50"
                data-testid="note-save-button"
              >
                {noteSaving ? "Speichern …" : "Notiz speichern"}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function InquiriesList({ inquiries, reload }) {
  const [replyTo, setReplyTo] = useState(null);

  if (inquiries.length === 0)
    return <p className="border border-white/10 bg-ink p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-600" data-testid="inquiries-empty">Noch keine Nachrichten</p>;

  return (
    <div className="space-y-4">
      {inquiries.map((q) => (
        <div key={q.id} className="border border-white/10 bg-ink p-6" data-testid={`inquiry-row-${q.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-serif text-xl text-white">{q.name}</p>
            {q.category && (
              <span className="rounded-full border border-white/15 px-3 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400">
                {TYPE_LABELS[q.category] || q.category}
              </span>
            )}
            {q.budget && (
              <span className="rounded-full border border-gold/30 px-3 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-gold">{q.budget}</span>
            )}
          </div>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            {q.email} {q.phone && `· ${q.phone}`} {q.date && `· Wunschtermin: ${q.date}`}
          </p>
          {q.created_at && (
            <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600">Eingegangen am {fmtCreated(q.created_at)} Uhr</p>
          )}
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-400">{q.message}</p>
          <button
            data-testid={`inquiry-reply-button-${q.id}`}
            onClick={() => setReplyTo(replyTo === q.id ? null : q.id)}
            className="mt-4 rounded-full border border-white/20 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:border-gold hover:text-gold"
          >
            Antworten
          </button>
          {replyTo === q.id && <ReplyBox endpoint={`/inquiries/${q.id}/message`} label={q.name} />}
        </div>
      ))}
    </div>
  );
}

function ClientGalleries({ galleries, reload }) {
  const [form, setForm] = useState({ title: "", client_name: "", client_email: "", welcome: "" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [favs, setFavs] = useState({});
  const fileRefs = useRef({});

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/client-galleries", form);
      toast.success("Galerie angelegt — jetzt Bilder hochladen und E-Mail senden.");
      setForm({ title: "", client_name: "", client_email: "", welcome: "" });
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const loadPhotos = async (g) => {
    try {
      const [photosRes, favsRes] = await Promise.all([
        api.get(`/galerie/${g.code}`),
        api.get(`/galerie/${g.code}/favorites`),
      ]);
      setPhotos(photosRes.data.photos);
      const map = {};
      favsRes.data.forEach((f) => (map[f.photo_id] = f.note || ""));
      setFavs(map);
    } catch {
      setPhotos([]);
      setFavs({});
    }
  };

  const extendGallery = async (g) => {
    try {
      const res = await api.post(`/client-galleries/${g.id}/extend`);
      toast.success(`Galerie verlängert — jetzt aktiv bis ${new Date(res.data.expires_at).toLocaleDateString("de-DE")}.`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleOpen = (g) => {
    if (open === g.id) {
      setOpen(null);
      setPhotos([]);
    } else {
      setOpen(g.id);
      loadPhotos(g);
    }
  };

  const upload = async (g) => {
    const input = fileRefs.current[g.id];
    if (!input?.files?.length) {
      toast.error("Bitte erst Bilder auswählen.");
      return;
    }
    const fd = new FormData();
    [...input.files].forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const res = await api.post(`/client-galleries/${g.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${res.data.uploaded} Bilder hochgeladen.`);
      input.value = "";
      reload();
      if (open === g.id) loadPhotos(g);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sendMail = async (g) => {
    try {
      await api.post(`/client-galleries/${g.id}/send`);
      toast.success(`E-Mail an ${g.client_email} verschickt.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const copyLink = (g) => {
    navigator.clipboard.writeText(`${window.location.origin}/galerie?code=${g.code}`);
    toast.success("Galerie-Link kopiert.");
  };

  const removeGallery = async (g) => {
    if (!window.confirm(`Galerie „${g.title}" mit allen Bildern löschen?`)) return;
    try {
      await api.delete(`/client-galleries/${g.id}`);
      toast.success("Galerie gelöscht.");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removePhoto = async (pid, g) => {
    try {
      await api.delete(`/client-photos/${pid}`);
      toast.success("Foto entfernt.");
      loadPhotos(g);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <form onSubmit={create} className="border border-white/10 bg-ink p-6" data-testid="client-gallery-form">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Neue Kunden-Galerie — mit privatem Zugangscode</p>
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div>
            <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Titel *</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="z. B. Hochzeit Sommer 2026" data-testid="cg-title-input" className="input-line" />
          </div>
          <div>
            <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Kundenname *</label>
            <input required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Name des Kunden" data-testid="cg-name-input" className="input-line" />
          </div>
          <div>
            <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Kunden-E-Mail *</label>
            <input required type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="kunde@beispiel.de" data-testid="cg-email-input" className="input-line" />
          </div>
        </div>
        <div className="mt-5">
          <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">
            Persönliche Begrüßung (optional — steht oben in der Galerie)
          </label>
          <textarea
            value={form.welcome}
            onChange={(e) => setForm({ ...form, welcome: e.target.value })}
            rows={2}
            placeholder="z. B. Es war so schön mit euch — hier sind eure Bilder!"
            data-testid="cg-welcome-input"
            className="input-line resize-none"
          />
        </div>
        <button type="submit" disabled={busy} data-testid="cg-create-button" className="mt-6 rounded-full bg-gold px-8 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-ink transition-colors hover:bg-white disabled:opacity-50">
          Galerie anlegen
        </button>
      </form>

      <div className="mt-8 space-y-4">
        {galleries.length === 0 ? (
          <p className="border border-white/10 bg-ink p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-600" data-testid="cg-empty">
            Noch keine Kunden-Galerien
          </p>
        ) : (
          galleries.map((g) => {
            const expired = g.expires_at && new Date(g.expires_at) < new Date();
            return (
            <div key={g.id} className={`border bg-ink p-6 ${expired ? "border-terracotta/40" : "border-white/10"}`} data-testid={`cg-row-${g.id}`}>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="font-serif text-xl text-white">{g.title}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    {g.client_name} · {g.client_email} · {g.photo_count} Fotos
                    {g.favorite_count > 0 ? ` · ${g.favorite_count} ♥` : ""}
                    {g.expires_at ? ` · aktiv bis ${new Date(g.expires_at).toLocaleDateString("de-DE")}` : ""}
                  </p>
                  {expired && (
                    <p className="mt-2 w-fit border border-terracotta/40 bg-terracotta/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-terracotta" data-testid={`cg-expired-${g.id}`}>
                      Abgelaufen — Kunde sieht die Galerie nicht mehr
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <span className="border border-gold/40 bg-ink2 px-3 py-1.5 font-mono text-sm tracking-[0.3em] text-gold" data-testid={`cg-code-${g.id}`}>
                      {g.code}
                    </span>
                    <button onClick={() => copyLink(g)} data-testid={`cg-copy-${g.id}`} className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-gold">
                      Link kopieren
                    </button>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <button onClick={() => toggleOpen(g)} data-testid={`cg-photos-toggle-${g.id}`} className="rounded-full border border-white/20 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:border-gold hover:text-gold">
                    Fotos ({g.photo_count})
                  </button>
                  <button onClick={() => extendGallery(g)} data-testid={`cg-extend-${g.id}`} className="flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:border-gold hover:text-gold">
                    <CalendarClock size={11} /> +3 Monate
                  </button>
                  <button onClick={() => sendMail(g)} data-testid={`cg-send-${g.id}`} className="rounded-full bg-gold px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-white">
                    E-Mail an Kunden
                  </button>
                  <button onClick={() => removeGallery(g)} data-testid={`cg-delete-${g.id}`} className="flex items-center gap-1 rounded-full border border-white/15 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 transition-colors hover:border-terracotta hover:text-terracotta">
                    <Trash2 size={11} /> Löschen
                  </button>
                </div>
              </div>
              <div className="mt-5 flex flex-col gap-3 border-t border-white/10 pt-5 sm:flex-row sm:items-center">
                <input
                  ref={(el) => (fileRefs.current[g.id] = el)}
                  type="file"
                  accept="image/*"
                  multiple
                  data-testid={`cg-files-${g.id}`}
                  className="input-line file:mr-4 file:rounded-full file:border-0 file:bg-gold file:px-4 file:py-1.5 file:font-mono file:text-[10px] file:uppercase file:text-ink"
                />
                <button onClick={() => upload(g)} disabled={busy} data-testid={`cg-upload-${g.id}`} className="shrink-0 rounded-full border border-white/20 px-6 py-2.5 font-mono text-[9px] uppercase tracking-[0.2em] text-white transition-colors hover:border-gold hover:text-gold disabled:opacity-50">
                  Bilder hochladen
                </button>
              </div>
              {open === g.id && (
                <div className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-8" data-testid={`cg-photos-${g.id}`}>
                  {photos.map((p) => (
                    <div key={p.id} className="group relative">
                      <img src={photoSrc({ storage_path: p.storage_path })} alt={p.filename} className="aspect-square w-full object-cover" loading="lazy" />
                      {favs[p.id] !== undefined && (
                        <span
                          className="absolute left-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-gold text-ink"
                          data-testid={`cg-photo-fav-${p.id}`}
                          title={favs[p.id] ? `Notiz: ${favs[p.id]}` : "Lieblingsbild"}
                        >
                          <Heart size={11} className="fill-ink" />
                        </span>
                      )}
                      {favs[p.id] && (
                        <p className="mt-1 line-clamp-2 font-mono text-[9px] leading-snug text-gold" data-testid={`cg-photo-note-${p.id}`}>
                          „{favs[p.id]}“
                        </p>
                      )}
                      <button
                        onClick={() => removePhoto(p.id, g)}
                        data-testid={`cg-photo-delete-${p.id}`}
                        aria-label="Foto löschen"
                        className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-ink/80 text-terracotta opacity-0 transition-opacity hover:bg-terracotta hover:text-white group-hover:opacity-100"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  ))}
                  {photos.length === 0 && <p className="col-span-full font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">Noch keine Bilder — lade oben welche hoch</p>}
                </div>
              )}
            </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function MomenteTab({ moments, reload }) {
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Bitte erst ein Foto auswählen.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    setBusy(true);
    try {
      await api.post("/moments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Moment veröffentlicht — sofort auf /momente sichtbar.");
      setCaption("");
      fileRef.current.value = "";
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm("Diesen Moment löschen?")) return;
    try {
      await api.delete(`/moments/${m.id}`);
      toast.success("Moment gelöscht.");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="border border-white/10 bg-ink p-6" data-testid="momente-upload-form">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
          Neuer Moment — direkt vom Handy hochladen
        </p>
        <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            data-testid="moment-file-input"
            className="input-line file:mr-4 file:rounded-full file:border-0 file:bg-gold file:px-4 file:py-1.5 file:font-mono file:text-[10px] file:uppercase file:text-ink"
          />
          <input
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Kurze Bildunterschrift (optional)"
            maxLength={200}
            data-testid="moment-caption-input"
            className="input-line"
          />
          <button
            onClick={upload}
            disabled={busy}
            data-testid="moment-upload-button"
            className="shrink-0 rounded-full bg-gold px-6 py-2.5 font-mono text-[9px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-white disabled:opacity-50"
          >
            {busy ? "Lädt …" : "Veröffentlichen"}
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="momente-grid">
        {moments.length === 0 ? (
          <p className="col-span-full border border-white/10 bg-ink p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-600">
            Noch keine Momente — lade dein erstes Foto hoch
          </p>
        ) : (
          moments.map((m) => (
            <div key={m.id} className="group relative border border-white/10" data-testid={`moment-row-${m.id}`}>
              <img src={photoSrc({ storage_path: m.storage_path })} alt={m.caption || "Moment"} className="aspect-[3/4] w-full object-cover" loading="lazy" />
              <button
                onClick={() => remove(m)}
                data-testid={`moment-delete-${m.id}`}
                aria-label="Moment löschen"
                className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-ink/80 text-terracotta opacity-0 transition-opacity hover:bg-terracotta hover:text-white group-hover:opacity-100"
              >
                <Trash2 size={11} />
              </button>
              {m.caption && <p className="truncate px-2 py-1.5 font-mono text-[9px] text-zinc-500">{m.caption}</p>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Tagesplan({ bookings, blocked }) {
  const [weekOffset, setWeekOffset] = useState(0);
  const todayISO = new Date().toISOString().slice(0, 10);
  const fmtDay = (d) =>
    new Date(d + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const fmtShort = (d) =>
    new Date(d + "T12:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "numeric" });

  const isBlocked = (d) => blocked.some((b) => d >= b.date_from && d <= b.date_to);
  const bookingsOn = (d) => bookings.filter((b) => b.date === d && b.status !== "abgelehnt");

  const base = new Date();
  const monday = new Date(base);
  monday.setDate(base.getDate() - ((base.getDay() + 6) % 7) + weekOffset * 7);
  const weekDays = [...Array(7)].map((_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });

  const byDay = {};
  bookings
    .filter((b) => b.date >= todayISO && b.status !== "abgelehnt")
    .forEach((b) => {
      (byDay[b.date] = byDay[b.date] || []).push(b);
    });

  const entries = [
    ...Object.keys(byDay).map((d) => ({ type: "day", date: d, items: byDay[d] })),
    ...blocked.filter((b) => b.date_to >= todayISO).map((b) => ({ type: "blocked", date: b.date_from, item: b })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  const printPlan = () => {
    const rows = entries
      .map((e) =>
        e.type === "blocked"
          ? `<tr><td style="white-space:nowrap">${fmtDay(e.item.date_from)}${e.item.date_to !== e.item.date_from ? " – " + fmtDay(e.item.date_to) : ""}</td><td style="color:#b0442c">GESPERRT${e.item.reason ? " — " + e.item.reason : ""}</td></tr>`
          : e.items
              .map(
                (b) =>
                  `<tr><td style="white-space:nowrap">${fmtDay(e.date)}</td><td><strong>${b.name}</strong> — ${TYPE_LABELS[b.shoot_type] || b.shoot_type} · ${TIME_SLOT_LABELS[b.time_slot] || "Flexibel"} · ${STATUS_LABELS[b.status]}${b.note ? `<br/><small style="color:#555">Notiz: ${b.note}</small>` : ""}</td></tr>`
              )
              .join("")
      )
      .join("");
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(
      `<!doctype html><html lang="de"><head><title>Tagesplan — Leno Fotografie</title><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>body{font-family:Arial,sans-serif;padding:28px;color:#111}h1{font-size:20px;margin:0}p.sub{color:#666;font-size:12px}table{width:100%;border-collapse:collapse;margin-top:16px}td{border-bottom:1px solid #ddd;padding:10px 8px;font-size:13px;vertical-align:top}</style></head><body><h1>Tagesplan — Leno Fotografie</h1><p class="sub">Stand: ${new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })} · Tipp: Am Handy über „Teilen“ als PDF sichern oder ausdrucken</p><table>${rows || '<tr><td>Keine kommenden Termine</td></tr>'}</table></body></html>`
    );
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <div className="space-y-8">
      <div className="border border-white/10 bg-ink p-6" data-testid="wochen-uebersicht">
        <div className="mb-5 flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Wochenübersicht</p>
          <div className="flex items-center gap-2">
            <button
              onClick={printPlan}
              data-testid="tagesplan-print-button"
              className="rounded-full border border-gold/40 px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-ink"
            >
              PDF / Drucken
            </button>
            <button
              onClick={() => setWeekOffset(weekOffset - 1)}
              data-testid="week-prev-button"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-zinc-400 transition-colors hover:border-gold hover:text-gold"
              aria-label="Vorherige Woche"
            >
              ‹
            </button>
            {weekOffset !== 0 && (
              <button
                onClick={() => setWeekOffset(0)}
                data-testid="week-today-button"
                className="rounded-full border border-gold/40 px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-gold"
              >
                Heute
              </button>
            )}
            <button
              onClick={() => setWeekOffset(weekOffset + 1)}
              data-testid="week-next-button"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-zinc-400 transition-colors hover:border-gold hover:text-gold"
              aria-label="Nächste Woche"
            >
              ›
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {weekDays.map((d) => {
            const items = bookingsOn(d);
            const blockedDay = isBlocked(d);
            return (
              <div
                key={d}
                data-testid={`week-day-${d}`}
                className={`min-h-[84px] border p-1.5 sm:p-2 ${blockedDay ? "border-terracotta/30 bg-terracotta/5" : "border-white/10 bg-ink2"} ${d === todayISO ? "ring-1 ring-gold" : ""}`}
              >
                <p className={`font-mono text-[8px] uppercase tracking-widest ${d === todayISO ? "text-gold" : "text-zinc-500"}`}>{fmtShort(d)}</p>
                {blockedDay && <p className="mt-1 font-mono text-[8px] uppercase tracking-widest text-terracotta">Gesperrt</p>}
                {items.map((b) => (
                  <p key={b.id} className="mt-1 truncate font-mono text-[8px] text-zinc-300">
                    · {b.name}
                  </p>
                ))}
                {items.length > 0 && (
                  <p className="mt-1 font-mono text-[8px] uppercase tracking-widest text-gold">{items.length}/3 belegt</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {entries.length === 0 ? (
        <p className="border border-white/10 bg-ink p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-600" data-testid="tagesplan-empty">
          Keine kommenden Termine — der Kalender ist frei
        </p>
      ) : (
        <div className="space-y-4" data-testid="tagesplan-list">
          {entries.map((e) =>
            e.type === "blocked" ? (
              <div key={`blocked-${e.item.id}`} className="border border-terracotta/30 bg-ink p-6" data-testid={`tagesplan-blocked-${e.item.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full border border-terracotta/40 px-3 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-terracotta">Gesperrt</span>
                  <p className="font-serif text-xl text-white">
                    {fmtDay(e.item.date_from)}
                    {e.item.date_to !== e.item.date_from ? ` — ${fmtDay(e.item.date_to)}` : ""}
                  </p>
                </div>
                {e.item.reason && <p className="mt-2 text-sm text-zinc-400">{e.item.reason}</p>}
              </div>
            ) : (
              <div key={e.date} className="border border-white/10 bg-ink p-6" data-testid={`tagesplan-day-${e.date}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-white/10 pb-4">
                  <p className="font-serif text-2xl text-white">{fmtDay(e.date)}</p>
                  <span className="rounded-full border border-gold/40 px-3 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-gold">
                    {e.items.length} {e.items.length === 1 ? "Shooting" : "Shootings"}
                  </span>
                </div>
                <div className="mt-4 space-y-3">
                  {e.items.map((b) => (
                    <div key={b.id} data-testid={`tagesplan-item-${b.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                            {TIME_SLOT_LABELS[b.time_slot] || "Flexibel"}
                          </span>
                          <p className="font-sans text-sm font-medium text-white">{b.name}</p>
                          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                            {TYPE_LABELS[b.shoot_type] || b.shoot_type}
                          </span>
                        </div>
                        <span className={`rounded-full border px-3 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] ${STATUS_COLORS[b.status]}`}>
                          {STATUS_LABELS[b.status]}
                        </span>
                      </div>
                      {b.note && <p className="mt-1.5 font-mono text-[10px] tracking-wide text-zinc-500">Notiz: {b.note}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

function BlockedDates({ blocked, reload }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const block = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/blocked-dates", { date_from: dateFrom, date_to: dateTo, reason });
      toast.success("Zeitraum gesperrt — die Tage sind ab sofort für Besucher als reserviert markiert.");
      setDateFrom("");
      setDateTo("");
      setReason("");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const unblock = async (id) => {
    try {
      await api.delete(`/blocked-dates/${id}`);
      toast.success("Zeitraum wieder freigegeben.");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="mb-8 border border-white/10 bg-ink p-6" data-testid="blocked-dates-panel">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Tage sperren — Urlaub & Privates</p>
      <form onSubmit={block} className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_2fr_auto]" data-testid="blocked-date-form">
        <div>
          <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Von *</label>
          <input
            type="date"
            required
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="blocked-date-from-input"
            className="input-line [color-scheme:dark]"
          />
        </div>
        <div>
          <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Bis (optional)</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="blocked-date-to-input"
            className="input-line [color-scheme:dark]"
          />
        </div>
        <div>
          <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Grund (optional)</label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="z. B. Urlaub"
            data-testid="blocked-reason-input"
            className="input-line"
          />
        </div>
        <div className="flex items-end">
          <button
            type="submit"
            disabled={busy}
            data-testid="blocked-date-submit"
            className="w-full rounded-full bg-gold px-7 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-white disabled:opacity-50 lg:w-auto"
          >
            {busy ? "…" : "Zeitraum sperren"}
          </button>
        </div>
      </form>
      {blocked.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2" data-testid="blocked-dates-list">
          {blocked.map((b) => (
            <span
              key={b.id}
              className="flex items-center gap-2 rounded-full border border-terracotta/40 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-terracotta"
              data-testid={`blocked-date-chip-${b.id}`}
            >
              {b.date_from}
              {b.date_to && b.date_to !== b.date_from ? ` – ${b.date_to}` : ""}
              {b.reason ? ` · ${b.reason}` : ""}
              <button onClick={() => unblock(b.id)} data-testid={`blocked-date-unblock-${b.id}`} aria-label="Freigeben" className="transition-colors hover:text-white">
                <Trash2 size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("fotos");
  const [photos, setPhotos] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [moments, setMoments] = useState([]);

  const reload = useCallback(() => {
    api.get("/photos").then((r) => setPhotos(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
    api.get("/blocked-dates").then((r) => setBlocked(r.data)).catch(() => {});
    api.get("/client-galleries").then((r) => setGalleries(r.data)).catch(() => {});
    api.get("/moments").then((r) => setMoments(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("sm_token")) {
      setUser(null);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("sm_token");
        setUser(null);
      });
  }, []);

  useEffect(() => {
    if (user) reload();
  }, [user, reload]);

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <Loader2 className="animate-spin text-gold" size={24} />
      </div>
    );
  }

  if (user === null) return <LoginForm onLogin={setUser} />;

  const TABS = [
    { key: "fotos", label: `Fotos (${photos.length})` },
    { key: "buchungen", label: `Terminanfragen (${bookings.length})` },
    { key: "tagesplan", label: "Tagesplan" },
    { key: "anfragen", label: `Nachrichten (${inquiries.length})` },
    { key: "kunden", label: `Kunden (${galleries.length})` },
    { key: "momente", label: `Momente (${moments.length})` },
  ];

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-ink2">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-5 lg:px-12">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-gold">Backoffice</p>
            <h1 className="mt-1 font-serif text-2xl text-white">Leno Fotografie — Verwaltung</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="rounded-full border border-white/15 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:border-gold hover:text-gold"
            >
              Website ansehen
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={() => {
                localStorage.removeItem("sm_token");
                setUser(null);
                toast.success("Abgemeldet.");
              }}
              className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:border-terracotta hover:text-terracotta"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-[1440px] gap-5 overflow-x-auto px-6 lg:gap-8 lg:px-12">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`relative whitespace-nowrap pb-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors ${tab === t.key ? "text-gold" : "text-zinc-500 hover:text-white"}`}
            >
              {t.label}
              {tab === t.key && <motion.span layoutId="admin-tab-line" className="absolute bottom-0 left-0 right-0 h-px bg-gold" />}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] px-6 py-10 lg:px-12">
        <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
          {[
            ["Fotos online", photos.length],
            ["Neue Anfragen", bookings.filter((b) => b.status === "neu").length],
            ["Bestätigte Termine", bookings.filter((b) => b.status === "bestaetigt").length],
            ["Nachrichten", inquiries.length],
          ].map(([label, val]) => (
            <div key={label} className="border border-white/10 bg-ink2 p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
              <p className="font-serif text-3xl text-white">{val}</p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
            </div>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
            {tab === "fotos" && <PhotoManager photos={photos} reload={reload} />}
            {tab === "buchungen" && (
              <>
                <PromoControl />
                <BlockedDates blocked={blocked} reload={reload} />
                <BookingsList bookings={bookings} reload={reload} />
              </>
            )}
            {tab === "tagesplan" && <Tagesplan bookings={bookings} blocked={blocked} />}
            {tab === "anfragen" && <InquiriesList inquiries={inquiries} reload={reload} />}
            {tab === "kunden" && <ClientGalleries galleries={galleries} reload={reload} />}
            {tab === "momente" && <MomenteTab moments={moments} reload={reload} />}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
